import { useState, useEffect, useCallback } from "react";
import moment from "moment";
import axios from "axios";
import { apiUrl } from "features/configure";
import { useOutletContext, useParams } from "react-router";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { selectSubEntities } from "features/metadesk/redux/entityConfig/entityConfig.selector";
import { sortArrayByKey } from "features/helpers/utils";
import { Box } from "@mui/material";
import { IconButton } from "@metacrm/metacrm-material-ui/dist/Button";
import MetaCRMTag from "@metacrm/metacrm-material-ui/dist/MetaCRMTag";
import { useTheme } from "@mui/styles";
import useNavigateEntity from "hooks/useNavigateEntity";
import { StyledStatus } from "./NotificationCenter.styles";
import { enqueueSnackbar } from "features/common/redux/actions";
import { get, debounce } from "lodash-es";
import useTableParams from "hooks/useTableParams";
import { useConfirm } from "features/home/ConfirmDialogProvider";

const useNotificationCenter = () => {
  const { navigateEntity } = useNavigateEntity();
  const [entityName] = useOutletContext();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const [notificationList, setNotificationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [refetchData, setRefetchData] = useState(false);

  const [actionsList, setActionsList] = useState([]);

  // 使用 useTableParams 管理篩選條件
  const { params, setParam, setParams } = useTableParams({
    page: 1,
    order: "desc",
    orderBy: "",
    searchText: "",
    selectedItems: { status: [] },
  });

  const { page, order, orderBy, searchText, selectedItems } = params;

  const handleDuplicate = async (notificationId) => {
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/notification/${entityName}/${notificationId}/duplicate`
      );
      navigateEntity(`/notification/notification-detail/${data._id}`);
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: get(error, "response.data.error", "error"),
        })
      );
    }
  };

  const handleMoveToDraft = async (notificationId) => {
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/notification/${entityName}/${notificationId}/moveToDraft`
      );
      setRefetchData(true);
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: get(error, "response.data.error", "error"),
        })
      );
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/notification/${entityName}/${notificationId}/delete`
      );
      setRefetchData(true);
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: get(error, "response.data.error", "error"),
        })
      );
    }
  };

  const handleDeleteConfirm = (notificationId) => {
    confirm({
      title: formatMessage({
        id: "notification.creation.deleteReminderTitle",
      }),
      content: formatMessage({
        id: "notification.creation.deleteReminderDescription",
      }),
      confirmText: formatMessage({
        id: "popup.confirmToDelete",
      }),
      cancelText: formatMessage({
        id: "global.btn.cancel",
      }),
    })
      .then(async () => {
        await handleDelete(notificationId);
      })
      .catch((err) => {
        console.log("cancel", err);
      });
  };

  const handleArchive = async (notificationId) => {
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/notification/${entityName}/${notificationId}/archive`
      );
      setRefetchData(true);
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: get(error, "response.data.error", "error"),
        })
      );
    }
  };

  const handleArchiveConfirm = (notificationId) => {
    confirm({
      title: formatMessage({
        id: "notification.create.archiveReminder",
      }),
      content: formatMessage({
        id: "notification.create.archiveReminderDescription",
      }),
      confirmText: formatMessage({
        id: "notification.create.archiveReminderbtn",
      }),
      cancelText: formatMessage({
        id: "global.btn.cancel",
      }),
    })
      .then(async () => {
        await handleArchive(notificationId);
      })
      .catch((err) => {
        console.log("cancel", err);
      });
  };

  const fetchData = useCallback(
    debounce(async () => {
      setLoading(true);
      try {
        const data = await axios.get(
          `${apiUrl}/api/notification/${entityName}`,
          {
            params: {
              search: searchText,
              sort: orderBy,
              order: order,
              page: page,
              status: selectedItems?.status,
            },
          }
        );
        setTotalItems(get(data, "headers.x-total-count"));
        setNotificationList(get(data, "data.notifications", []));
      } catch (error) {
        dispatch(
          enqueueSnackbar({
            message: get(error, "response.data.error", "error"),
          })
        );
      } finally {
        setLoading(false);
      }
    }, 500),
    [entityName, order, orderBy, page, searchText, selectedItems?.status]
  );
  const commonActions = (itemData, queryString) => [
    {
      children: <Box>{formatMessage({ id: "global.function.detail" })}</Box>,
      func: (e) =>
        navigateEntity(
          `/notification/notification-detail/${itemData._id}${queryString}`
        ),
    },
    {
      children: <Box>{formatMessage({ id: "global.function.duplicate" })}</Box>,
      func: (e) => handleDuplicate(itemData._id),
    },
  ];

  const statusActionsMap = (itemData) => ({
    sent: [
      {
        children: (
          <Box>{formatMessage({ id: "global.function.viewReport" })}</Box>
        ),
        func: (e) =>
          navigateEntity(
            `/notification/notification-report?page=1&&order=desc&searchText=${
              itemData.title
            }&dateRange=[${moment(itemData.sendTime)
              .utc()
              .startOf("day")
              .valueOf()},${moment().utc().endOf("day").valueOf()}]`
          ),
      },
      {
        children: (
          <Box color={theme.customColors.pink[600]}>
            {formatMessage({ id: "notification.btn.archive" })}
          </Box>
        ),
        func: (e) => handleArchiveConfirm(itemData._id),
      },
    ],
    scheduled: [
      {
        children: (
          <Box>{formatMessage({ id: "global.function.moveToDraft" })}</Box>
        ),
        func: (e) => handleMoveToDraft(itemData._id),
      },
      {
        children: (
          <Box color={theme.customColors.pink[600]}>
            {formatMessage({ id: "notification.btn.delete" })}
          </Box>
        ),
        func: (e) => handleDeleteConfirm(itemData._id),
      },
    ],
    draft: [
      {
        children: (
          <Box color={theme.customColors.pink[600]}>
            {formatMessage({ id: "notification.btn.delete" })}
          </Box>
        ),
        func: (e) => handleDeleteConfirm(itemData._id),
      },
    ],
    archived: [
      {
        children: (
          <Box>{formatMessage({ id: "global.function.viewReport" })}</Box>
        ),
        func: (e) =>
          navigateEntity(
            `/notification/notification-report?page=1&&order=desc&searchText=${
              itemData.title
            }&dateRange=[${moment(itemData.sendTime)
              .utc()
              .startOf("day")
              .valueOf()},${moment().utc().endOf("day").valueOf()}]`
          ),
      },
    ],
  });

  const handleSetSelectedItems = (selectedItemsValue) => {
    setParams({
      selectedItems: {
        status: selectedItems.status.includes(selectedItemsValue.value)
          ? selectedItems.status.filter(
              (item) => item !== selectedItemsValue.value
            ) // 移除該值
          : [...selectedItems.status, selectedItemsValue.value],
      },
      page: 1,
    });
  };

  const handleClearSelectedItems = () => {
    setParams({
      selectedItems: {
        status: [],
      },
      page: 1,
    });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setParams({ order: isAsc ? "desc" : "asc", orderBy: property });
  };

  const handleClickActions = (event, itemData) => {
    event.stopPropagation();
    const queryString = window.location.search;

    setActionsList([
      ...commonActions(itemData, queryString),
      ...(statusActionsMap(itemData)[itemData.status] || []),
    ]);

    setAnchorEl(event.currentTarget);
  };

  const handleCloseActions = () => {
    setAnchorEl(null);
  };

  const renderContents = useCallback((itemData) => {
    const contents = [
      <Box slot="status">
        <StyledStatus status={itemData.status}>
          {formatMessage({ id: `global.campaignStatus.${itemData.status}` })}
        </StyledStatus>
      </Box>,
      <Box slot="notification">{itemData.title}</Box>,
      <Box
        slot="targetAudience"
        sx={{
          maxWidth: "150px",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {itemData?.targetAudiences?.length > 0 ? (
          <Box display="flex" alignItems="center">
            {itemData.targetAudiences.slice(0, 2).map((tag) => (
              <MetaCRMTag
                key={tag.value}
                option={{
                  color: get(tag, "color", "#E6E6E6"),
                  label: tag.label || tag.value,
                }}
              />
            ))}
            {itemData.targetAudiences.length > 2 && (
              <Box color="#7b61ff" fontSize={12} ml="4px">
                +{itemData.targetAudiences.length - 2}
              </Box>
            )}
          </Box>
        ) : (
          "-"
        )}
      </Box>,
      <Box slot="channel">
        {get(itemData.channels, "widget") && (
          <Box
            component="i"
            mr={"4px"}
            className="meta-crm-icon-ic_channel_widget font-size-16"
          />
        )}
        {get(itemData.channels, "discord") && (
          <Box
            component="i"
            mr={"4px"}
            className="meta-crm-icon-ic_channel_discord font-size-16"
          />
        )}
        {get(itemData.channels, "telegram") && (
          <Box
            component="i"
            mr={"4px"}
            className="meta-crm-icon-ic_channel_telegram font-size-16"
          />
        )}

        {get(itemData.channels, "email") && (
          <Box
            component="i"
            mr={"4px"}
            className="meta-crm-icon-ic_channel_email font-size-16"
          />
        )}

        {get(itemData.channels, "xmtp") && (
          <Box
            component="i"
            mr={"4px"}
            className="meta-crm-icon-ic_channel_xmtp font-size-16"
          />
        )}
      </Box>,
      <Box slot="sendTime">
        {itemData.sendTime
          ? moment(itemData.sendTime).format("MMM D, YYYY HH:mm")
          : "-"}
      </Box>,
      <Box slot="actions">
        {" "}
        <IconButton
          onClick={(e) => handleClickActions(e, itemData)}
          size={24}
          color={theme.customColors.purple[500]}
        >
          <i className="meta-crm-icon-ic_dots font-size-24" />
        </IconButton>
      </Box>,
    ];

    return contents;
  }, []);

  useEffect(() => {
    fetchData();
    return () => fetchData.cancel();
  }, [searchText, selectedItems, page, order, orderBy]);

  useEffect(() => {
    const fetchDataAsync = async () => {
      if (refetchData) {
        await fetchData();
        setRefetchData(false);
      }
    };
    fetchDataAsync();
  }, [refetchData]);

  const handleChangePage = (newPage) => {
    // setPage(newPage);
    setParams({ page: newPage });
  };

  const handleSearchChange = (event) => {
    setParams({ searchText: event.target.value, page: 1 });
  };

  const handleCategoryChange = (event, newValue) => {
    setParams({ category: newValue, page: 1 });
  };

  return {
    searchText,
    selectedItems,
    handleSetSelectedItems,
    handleClearSelectedItems,
    notificationList,
    setNotificationList,
    renderContents,
    page,
    totalItems,
    handleChangePage,
    handleSearchChange,
    handleCategoryChange,
    order,
    orderBy,
    handleRequestSort,
    loading,
    anchorEl,
    actionsList,
    handleCloseActions,
  };
};

export default useNotificationCenter;
